import { companyClient } from "api/api";
import { Button } from "components/button";
import { useModalContext } from "components/modal";
import Assignee from "components/react-select/assignees";
import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { TTargetType } from "types/target-type";

type AssigneeOption = { value: number; label: string };

const buildAssigneeOption = (
	fkUserId?: string | null,
	tech?: string | null
): AssigneeOption | null => {
	if (!fkUserId) return null;
	const value = Number(fkUserId);
	if (Number.isNaN(value)) return null;
	return { value, label: tech?.trim() || "" };
};

interface IProps {
	targetId: number;
	fkUserId?: string | null;
	tech?: string | null;
	lineItemsId?: string;
	lineItemsType?: TTargetType;
	/** When set, closes inline UI instead of replacing the global modal. */
	onClose?: () => void;
}

const ProductionAssigneeSelect = ({
	targetId,
	fkUserId,
	tech,
	lineItemsId,
	lineItemsType,
	onClose,
}: IProps) => {
	const { t } = useTranslation();
	const tBase = "containers.lineItems.components.ProductionAssigneeSelect";
	const tr = (key: string) => t(`${tBase}.${key}`);
	const queryClient = useQueryClient();
	const { setModal } = useModalContext();
	const [assignee, setAssignee] = useState<AssigneeOption | null>(() =>
		buildAssigneeOption(fkUserId, tech)
	);

	useEffect(() => {
		setAssignee(buildAssigneeOption(fkUserId, tech));
	}, [fkUserId, tech, targetId]);

	const handleClose = () => {
		if (onClose) {
			onClose();
			return;
		}
		setModal();
	};

	const handleCancel = () => {
		handleClose();
	};

	const invalidateLineItems = () => {
		if (lineItemsId && lineItemsType) {
			queryClient.invalidateQueries(["products", lineItemsId, lineItemsType]);
			return;
		}
		queryClient.invalidateQueries(["products"]);
	};

	const handleSave = async () => {
		try {
			const response = await companyClient({
				method: "POST",
				url: "/products/updateAssignee",
				data: {
					assignee,
					targetId,
				},
			});

			if (response.status === 200) {
				invalidateLineItems();
				handleClose();
			}
		} catch (error) {
			console.error(error);
		}
	};

	const handleSetAssignee = (selected: AssigneeOption | null) => {
		setAssignee(selected);
	};

	return (
		<div>
			<Assignee
				isMulti={false}
				value={assignee}
				onChange={handleSetAssignee}
				menuPlacement={onClose ? "top" : "auto"}
			/>
			<div className="flex justify-between mt-2">
				<Button onClick={handleCancel}>{tr("Cancel")}</Button>
				<Button primary onClick={handleSave}>
					{tr("Save")}
				</Button>
			</div>
		</div>
	);
};

export { ProductionAssigneeSelect };
